import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiPlus,
  FiCalendar,
  FiUser,
  FiShield,
  FiActivity,
  FiCheck,
  FiXCircle,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import '../styles/LeaveSection.css';
import AssignLeave from './AssignLeave';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LEAVE_TABS = ['queue', 'calendar', 'balances'];

const LEAVE_REQUESTS_QUERY = `
  query AdminLeaveRequests {
    leaveRequests {
      id
      name
      employeeId
      leaveType
      duration
      startDate
      endDate
      hour
      reason
      status
      managerFeedback
      appliedDate
      createdAt
    }
  }
`;

const UPDATE_LEAVE_STATUS = `
  mutation UpdateLeaveRequestStatus($id: String!, $status: String!, $managerFeedback: String) {
    updateLeaveRequestStatus(id: $id, status: $status, managerFeedback: $managerFeedback) {
      id
      status
      managerFeedback
    }
  }
`;

const AVATAR_COLORS = [
  '#60a5fa',
  '#34d399',
  '#a78bfa',
  '#f472b6',
  '#fb923c',
  '#38bdf8',
  '#fbbf24',
  '#2dd4bf',
];

function formatLeaveDate(row) {
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  const start = row.startDate ? new Date(row.startDate) : null;
  const end = row.endDate ? new Date(row.endDate) : null;

  if (start && !Number.isNaN(start.getTime())) {
    if (end && !Number.isNaN(end.getTime()) && end.getTime() !== start.getTime()) {
      return `${start.toLocaleDateString('en-AU', opts)} - ${end.toLocaleDateString('en-AU', opts)}`;
    }
    return start.toLocaleDateString('en-AU', opts);
  }

  if (row.appliedDate) {
    const applied = new Date(row.appliedDate);
    if (!Number.isNaN(applied.getTime())) {
      return applied.toLocaleDateString('en-AU', opts);
    }
  }

  return '—';
}

function getCreatedTimestamp(row) {
  const created = row.createdAt ? new Date(row.createdAt) : null;
  if (created && !Number.isNaN(created.getTime())) return created.getTime();
  const applied = row.appliedDate ? new Date(row.appliedDate) : null;
  if (applied && !Number.isNaN(applied.getTime())) return applied.getTime();
  return 0;
}

function formatApplicationId(sequence) {
  return `LV-${String(sequence).padStart(3, '0')}`;
}

function initials(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
}

function avatarColor(name) {
  const s = String(name || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash + s.charCodeAt(i) * 17) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hash];
}

function parseDayCount(row) {
  const duration = String(row.duration || '').toLowerCase();
  if (duration.includes('hour') || row.hour) {
    const hours = Number(row.hour) || Number(duration.match(/(\d+)/)?.[1]) || 0;
    return hours > 0 ? Math.max(0.5, Math.round((hours / 8) * 10) / 10) : 0.5;
  }
  if (duration.includes('single')) return 1;

  const start = row.startDate ? new Date(row.startDate) : null;
  const end = row.endDate ? new Date(row.endDate) : null;
  if (
    start &&
    end &&
    !Number.isNaN(start.getTime()) &&
    !Number.isNaN(end.getTime())
  ) {
    const diff = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
    if (diff > 0) return diff;
  }

  const match = duration.match(/(\d+)/);
  if (match) return Number(match[1]) || 1;
  return 1;
}

function formatDaysLabel(days) {
  if (!days) return '—';
  if (Number.isInteger(days)) return `${days}d`;
  return `${days}d`;
}

function normalizeLeaveType(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('annual') || t.includes('annual leave') || t.includes('annual')) {
    return 'Annual Leave';
  }
  if (t.includes('personal') || t.includes('sick') || t.includes('casual')) {
    return 'Personal Leave';
  }
  if (t.includes('compassionate') || t.includes('bereavement')) {
    return 'Compassionate';
  }
  if (t.includes('unpaid')) return 'Unpaid Leave';
  return type || 'Other';
}

function leaveTypeTone(typeKey) {
  const t = String(typeKey || '').toLowerCase();
  if (t.includes('annual')) return 'annual';
  if (t.includes('personal') || t.includes('sick')) return 'personal';
  if (t.includes('compassionate')) return 'compassionate';
  if (t.includes('unpaid')) return 'unpaid';
  return 'other';
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseLeaveDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function buildMonthCells(anchor) {
  const first = startOfMonth(anchor);
  const startPad = first.getDay();
  const daysInMonth = new Date(
    anchor.getFullYear(),
    anchor.getMonth() + 1,
    0,
  ).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** True when `day` falls on this leave (inclusive range). */
function leaveOccursOnDay(leave, day) {
  const start =
    parseLeaveDate(leave.startDate) || parseLeaveDate(leave.appliedDate);
  if (!start) return false;
  const end = parseLeaveDate(leave.endDate) || start;
  const t = day.getTime();
  return t >= start.getTime() && t <= end.getTime();
}

function statusClass(rawStatus) {
  const s = normStatus(rawStatus);
  if (s === 'approved') return 'approved';
  if (s === 'pending') return 'pending';
  return 'declined';
}

function isDateInThisWeek(dateValue) {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() + mondayOffset);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return date >= start && date <= end;
}

function leaveOverlapsThisWeek(row) {
  const start = row.startDate ? new Date(row.startDate) : null;
  const end = row.endDate ? new Date(row.endDate) : start;
  if (!start || Number.isNaN(start.getTime())) {
    return isDateInThisWeek(row.appliedDate);
  }
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const weekStart = new Date(now);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(now.getDate() + mondayOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  const leaveEnd = end && !Number.isNaN(end.getTime()) ? end : start;
  return start <= weekEnd && leaveEnd >= weekStart;
}

function mapLeaveRowsWithApplicationIds(rows) {
  const sorted = [...rows].sort(
    (a, b) => getCreatedTimestamp(a) - getCreatedTimestamp(b),
  );
  const applicationIdByRowId = new Map();
  sorted.forEach((row, index) => {
    applicationIdByRowId.set(row.id, formatApplicationId(index + 1));
  });

  return rows.map((row) => {
    const days = parseDayCount(row);
    const uiStatus = row.status === 'Rejected' ? 'Declined' : row.status;
    return {
      id: row.id,
      name: row.name || '—',
      employeeId: row.employeeId,
      applicationId: applicationIdByRowId.get(row.id) || '—',
      type: row.leaveType || '—',
      typeKey: normalizeLeaveType(row.leaveType),
      date: formatLeaveDate(row),
      days,
      daysLabel: formatDaysLabel(days),
      duration: row.duration || (row.hour ? `${row.hour} Hour(s)` : '—'),
      status: uiStatus,
      rawStatus: row.status,
      reason: row.reason,
      managerFeedback: row.managerFeedback,
      startDate: row.startDate,
      endDate: row.endDate,
      createdAt: row.createdAt,
      appliedDate: row.appliedDate,
      avatar: avatarColor(row.name),
      initials: initials(row.name),
    };
  });
}

function normStatus(status) {
  return String(status || '').trim().toLowerCase();
}

function remainingBalanceLabel(leaves, leave) {
  const used = leaves
    .filter(
      (l) =>
        l.employeeId === leave.employeeId &&
        l.typeKey === leave.typeKey &&
        normStatus(l.rawStatus) === 'approved',
    )
    .reduce((sum, l) => sum + (l.days || 0), 0);
  const defaults = {
    'Annual Leave': 20,
    'Personal Leave': 10,
    Compassionate: 5,
    'Unpaid Leave': 30,
  };
  const base = defaults[leave.typeKey] ?? 15;
  const remaining = Math.max(0, base - used);
  return `${remaining}d remaining`;
}

export default function LeaveSection() {
  const { get, patch } = useAdminPageSearch();
  const urlTab = get('tab');
  const view = LEAVE_TABS.includes(urlTab) ? urlTab : 'queue';
  const showAssignLeave = get('action') === 'add';
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => new Date());

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await graphqlRequest(LEAVE_REQUESTS_QUERY);
      const rows = Array.isArray(data?.leaveRequests) ? data.leaveRequests : [];
      setLeaves(mapLeaveRowsWithApplicationIds(rows));
    } catch (err) {
      console.error('Error fetching leave requests:', err);
      setLeaves([]);
      setError(err?.message || 'Failed to load leave applications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLeaves();
  }, [fetchLeaves]);

  const pendingCount = useMemo(
    () => leaves.filter((l) => normStatus(l.rawStatus) === 'pending').length,
    [leaves],
  );

  const onLeaveThisWeek = useMemo(() => {
    const ids = new Set();
    leaves.forEach((l) => {
      if (normStatus(l.rawStatus) !== 'approved') return;
      if (leaveOverlapsThisWeek(l)) ids.add(l.employeeId || l.id);
    });
    return ids.size;
  }, [leaves]);

  const overview = useMemo(() => {
    const sumBy = (key) =>
      leaves
        .filter((l) => l.typeKey === key)
        .reduce((sum, l) => sum + (l.days || 0), 0);

    return [
      {
        id: 'annual',
        label: 'Annual Leave',
        value: sumBy('Annual Leave'),
        icon: FiCalendar,
        tone: 'blue',
      },
      {
        id: 'personal',
        label: 'Personal Leave',
        value: sumBy('Personal Leave'),
        icon: FiUser,
        tone: 'green',
      },
      {
        id: 'compassionate',
        label: 'Compassionate',
        value: sumBy('Compassionate'),
        icon: FiShield,
        tone: 'purple',
      },
      {
        id: 'unpaid',
        label: 'Unpaid Leave',
        value: sumBy('Unpaid Leave'),
        icon: FiActivity,
        tone: 'orange',
      },
    ];
  }, [leaves]);

  const queueLeaves = useMemo(() => {
    const rank = { pending: 0, approved: 1, rejected: 2, declined: 2 };
    return [...leaves].sort((a, b) => {
      const ra = rank[normStatus(a.rawStatus)] ?? 9;
      const rb = rank[normStatus(b.rawStatus)] ?? 9;
      if (ra !== rb) return ra - rb;
      return getCreatedTimestamp(b) - getCreatedTimestamp(a);
    });
  }, [leaves]);

  const balanceRows = useMemo(() => {
    const byEmployee = new Map();
    leaves.forEach((l) => {
      const key = l.employeeId || l.name;
      if (!byEmployee.has(key)) {
        byEmployee.set(key, {
          key,
          name: l.name,
          employeeId: l.employeeId,
          avatar: l.avatar,
          initials: l.initials,
          annual: 0,
          personal: 0,
          compassionate: 0,
          unpaid: 0,
        });
      }
      const row = byEmployee.get(key);
      if (normStatus(l.rawStatus) !== 'approved') return;
      if (l.typeKey === 'Annual Leave') row.annual += l.days || 0;
      else if (l.typeKey === 'Personal Leave') row.personal += l.days || 0;
      else if (l.typeKey === 'Compassionate') row.compassionate += l.days || 0;
      else if (l.typeKey === 'Unpaid Leave') row.unpaid += l.days || 0;
    });
    return [...byEmployee.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [leaves]);

  const calendarItems = useMemo(() => {
    return leaves
      .filter((l) => normStatus(l.rawStatus) !== 'rejected')
      .map((l) => ({
        ...l,
        sortKey: l.startDate ? new Date(l.startDate).getTime() : 0,
      }))
      .sort((a, b) => a.sortKey - b.sortKey);
  }, [leaves]);

  const monthCells = useMemo(() => buildMonthCells(monthAnchor), [monthAnchor]);

  const monthLabel = useMemo(
    () =>
      monthAnchor.toLocaleDateString('en-AU', {
        month: 'long',
        year: 'numeric',
      }),
    [monthAnchor],
  );

  const leavesByDayKey = useMemo(() => {
    const map = new Map();
    calendarItems.forEach((leave) => {
      monthCells.forEach((day) => {
        if (!day || !leaveOccursOnDay(leave, day)) return;
        const key = dayKey(day);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(leave);
      });
    });
    return map;
  }, [calendarItems, monthCells]);

  const selectedDayLeaves = useMemo(() => {
    return leavesByDayKey.get(dayKey(selectedDay)) || [];
  }, [leavesByDayKey, selectedDay]);

  const updateStatus = async (leave, nextStatus) => {
    if (!leave?.id) return;
    setUpdatingId(leave.id);
    try {
      await graphqlRequest(UPDATE_LEAVE_STATUS, {
        id: leave.id,
        status: nextStatus,
        managerFeedback: null,
      });
      toast.success(
        nextStatus === 'Approved'
          ? 'Leave request approved.'
          : 'Leave request denied.',
      );
      await fetchLeaves();
    } catch (err) {
      toast.error(err?.message || 'Could not update leave request.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="lm-page">
      <header className="lm-header">
        <div>
          <h1 className="lm-title">Leave Management</h1>
          <p className="lm-subtitle">
            {pendingCount} pending approval{pendingCount === 1 ? '' : 's'} ·{' '}
            {onLeaveThisWeek} employee{onLeaveThisWeek === 1 ? '' : 's'} on leave
            this week
          </p>
        </div>
        <button
          type="button"
          className="lm-btn-primary"
          onClick={() => patch({ action: 'add' })}
        >
          <FiPlus />
          New Leave Request
        </button>
      </header>

      {error ? (
        <div className="lm-error">
          <span>{error}</span>
          <button type="button" onClick={() => void fetchLeaves()}>
            Retry
          </button>
        </div>
      ) : null}

      <section className="lm-overview" aria-label="Leave overview">
        {overview.map((card) => {
          const Icon = card.icon;
          return (
            <article key={card.id} className="lm-overview-card">
              <div className={`lm-overview-icon lm-overview-icon--${card.tone}`}>
                <Icon />
              </div>
              <div>
                <div className="lm-overview-label">{card.label}</div>
                <div className="lm-overview-value">{card.value} days</div>
              </div>
            </article>
          );
        })}
      </section>

      <div className="lm-tabs" role="tablist">
        {[
          { id: 'queue', label: 'Queue' },
          { id: 'calendar', label: 'Calendar' },
          { id: 'balances', label: 'Balances' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={view === tab.id}
            className={`lm-tab${view === tab.id ? ' is-active' : ''}`}
            onClick={() => patch({ tab: tab.id === 'queue' ? '' : tab.id })}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {view === 'queue' ? (
        <div className="lm-table-wrap">
          <table className="lm-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Leave Type</th>
                <th>Dates</th>
                <th>Days</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="lm-empty">
                    Loading leave applications…
                  </td>
                </tr>
              ) : queueLeaves.length === 0 ? (
                <tr>
                  <td colSpan="7" className="lm-empty">
                    No leave applications yet. Use New Leave Request or have
                    employees submit leave from their portal.
                  </td>
                </tr>
              ) : (
                queueLeaves.map((leave) => {
                  const pending = normStatus(leave.rawStatus) === 'pending';
                  const approved = normStatus(leave.rawStatus) === 'approved';
                  const busy = updatingId === leave.id;
                  return (
                    <tr key={leave.id}>
                      <td>
                        <div className="lm-employee">
                          <div
                            className="lm-avatar"
                            style={{ background: leave.avatar }}
                          >
                            {leave.initials}
                          </div>
                          <div>
                            <div className="lm-employee-name">{leave.name}</div>
                            <div className="lm-employee-id">
                              {leave.applicationId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{leave.type}</td>
                      <td>{leave.date}</td>
                      <td>{leave.daysLabel}</td>
                      <td className="lm-balance">
                        {remainingBalanceLabel(leaves, leave)}
                      </td>
                      <td>
                        <span
                          className={`lm-status lm-status--${
                            approved
                              ? 'approved'
                              : pending
                                ? 'pending'
                                : 'declined'
                          }`}
                        >
                          {approved
                            ? 'Approved'
                            : pending
                              ? 'Pending'
                              : 'Denied'}
                        </span>
                      </td>
                      <td>
                        {pending ? (
                          <div className="lm-actions">
                            <button
                              type="button"
                              className="lm-approve"
                              disabled={busy}
                              onClick={() => void updateStatus(leave, 'Approved')}
                            >
                              <FiCheck />
                              Approve
                            </button>
                            <button
                              type="button"
                              className="lm-deny"
                              disabled={busy}
                              onClick={() => void updateStatus(leave, 'Rejected')}
                            >
                              <FiXCircle />
                              Deny
                            </button>
                          </div>
                        ) : (
                          <span className="lm-processed">Processed</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {view === 'calendar' ? (
        <div className="lm-calendar">
          <div className="lm-cal-toolbar">
            <button
              type="button"
              className="lm-cal-nav"
              onClick={() =>
                setMonthAnchor(
                  new Date(
                    monthAnchor.getFullYear(),
                    monthAnchor.getMonth() - 1,
                    1,
                  ),
                )
              }
              aria-label="Previous month"
            >
              <FiChevronLeft />
            </button>
            <h3 className="lm-cal-month">{monthLabel}</h3>
            <button
              type="button"
              className="lm-cal-nav"
              onClick={() =>
                setMonthAnchor(
                  new Date(
                    monthAnchor.getFullYear(),
                    monthAnchor.getMonth() + 1,
                    1,
                  ),
                )
              }
              aria-label="Next month"
            >
              <FiChevronRight />
            </button>
            <button
              type="button"
              className="lm-cal-today"
              onClick={() => {
                const now = new Date();
                setMonthAnchor(startOfMonth(now));
                setSelectedDay(now);
              }}
            >
              Today
            </button>
          </div>

          {loading ? (
            <p className="lm-empty">Loading calendar…</p>
          ) : (
            <>
              <div className="lm-cal-grid" role="grid" aria-label="Leave calendar">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="lm-cal-weekday">
                    {d}
                  </div>
                ))}
                {monthCells.map((day, idx) => {
                  if (!day) {
                    return (
                      <div key={`empty-${idx}`} className="lm-cal-cell is-empty" />
                    );
                  }
                  const key = dayKey(day);
                  const items = leavesByDayKey.get(key) || [];
                  const isToday = sameDay(day, new Date());
                  const isSelected = sameDay(day, selectedDay);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`lm-cal-cell${isToday ? ' is-today' : ''}${
                        isSelected ? ' is-selected' : ''
                      }${items.length ? ' has-events' : ''}`}
                      onClick={() => setSelectedDay(day)}
                    >
                      <span className="lm-cal-daynum">{day.getDate()}</span>
                      <div className="lm-cal-events">
                        {items.slice(0, 3).map((ev) => (
                          <span
                            key={ev.id}
                            className={`lm-cal-pill lm-cal-pill--${leaveTypeTone(
                              ev.typeKey,
                            )} lm-cal-pill--${statusClass(ev.rawStatus)}`}
                            title={`${ev.name} · ${ev.type} · ${ev.status}`}
                          >
                            {ev.name}
                          </span>
                        ))}
                        {items.length > 3 ? (
                          <span className="lm-cal-more">
                            +{items.length - 3} more
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="lm-cal-detail">
                <div className="lm-cal-detail-head">
                  <h4>
                    {selectedDay.toLocaleDateString('en-AU', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </h4>
                  <button
                    type="button"
                    className="lm-btn-primary"
                    onClick={() => patch({ action: 'add' })}
                  >
                    <FiPlus /> New Leave
                  </button>
                </div>
                {selectedDayLeaves.length === 0 ? (
                  <p className="lm-cal-empty">No leave on this day.</p>
                ) : (
                  <ul className="lm-cal-list">
                    {selectedDayLeaves.map((item) => (
                      <li key={item.id} className="lm-cal-item">
                        <div
                          className="lm-avatar"
                          style={{ background: item.avatar }}
                        >
                          {item.initials}
                        </div>
                        <div className="lm-cal-item-body">
                          <strong>{item.name}</strong>
                          <span>
                            {item.type} · {item.daysLabel} · {item.date}
                          </span>
                          {item.reason ? <span>{item.reason}</span> : null}
                        </div>
                        <span
                          className={`lm-status lm-status--${statusClass(
                            item.rawStatus,
                          )}`}
                        >
                          {item.status === 'Declined' ? 'Denied' : item.status}
                        </span>
                        {normStatus(item.rawStatus) === 'pending' ? (
                          <div className="lm-actions">
                            <button
                              type="button"
                              className="lm-approve"
                              disabled={updatingId === item.id}
                              onClick={() => void updateStatus(item, 'Approved')}
                              title="Approve"
                            >
                              <FiCheck /> Approve
                            </button>
                            <button
                              type="button"
                              className="lm-deny"
                              disabled={updatingId === item.id}
                              onClick={() => void updateStatus(item, 'Rejected')}
                              title="Deny"
                            >
                              <FiXCircle /> Deny
                            </button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}

      {view === 'balances' ? (
        <div className="lm-table-wrap">
          <table className="lm-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Annual Used</th>
                <th>Personal Used</th>
                <th>Compassionate Used</th>
                <th>Unpaid Used</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="lm-empty">
                    Loading balances…
                  </td>
                </tr>
              ) : balanceRows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="lm-empty">
                    No balance data yet.
                  </td>
                </tr>
              ) : (
                balanceRows.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <div className="lm-employee">
                        <div
                          className="lm-avatar"
                          style={{ background: row.avatar }}
                        >
                          {row.initials}
                        </div>
                        <div>
                          <div className="lm-employee-name">{row.name}</div>
                          <div className="lm-employee-id">
                            {row.employeeId || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{row.annual}d</td>
                    <td>{row.personal}d</td>
                    <td>{row.compassionate}d</td>
                    <td>{row.unpaid}d</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {showAssignLeave ? (
        <AssignLeave
          onClose={() => patch({}, ['action'])}
          onLeaveAssigned={() => {
            patch({}, ['action']);
            void fetchLeaves();
          }}
        />
      ) : null}
    </div>
  );
}
